import { useCallback, useEffect, useState } from 'react';
import { adminApi } from './api';
import ConfirmDialog from './ConfirmDialog';

const OPERATOR = { orange: 'Orange Money', moov: 'Moov Money' };

export default function MobileMoneySandboxAdmin({ enabled }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [decision, setDecision] = useState(null);

  const load = useCallback(async () => {
    if (!enabled) return;
    setLoading(true); setError('');
    try {
      const response = await adminApi.mobileMoneyPending();
      setItems(response.payments || []);
    } catch (e) { setError(e.message || 'Impossible de lire les demandes Mobile Money'); }
    finally { setLoading(false); }
  }, [enabled]);
  useEffect(() => { load(); }, [load]);

  async function resolve(item, outcome) {
    setBusy(item.id); setError(''); setNotice('');
    try {
      await adminApi.simulateMobileMoneyPayment(item.id, outcome);
      setNotice('Réponse de l’opérateur fictif enregistrée : ' + item.reference);
      await load();
    } catch (e) { setError(e.message || 'Simulation indisponible'); }
    finally { setBusy(''); setDecision(null); }
  }

  return <section className="bo-panel" aria-label="Simulateur opérateur Mobile Money">
    <div className="bo-panel__header">
      <div>
        <h3>Simulateur opérateur Mobile Money</h3>
        <p className="bo-muted">Orange Money et Moov Money · Environnement fictif, sans argent réel.
          Les demandes automatiques sont validées par le serveur ; les boutons de simulation
          servent uniquement aux scénarios configurés en mode manuel.</p>
      </div>
      <button type="button" className="bo-button bo-button--ghost"
        disabled={!enabled || loading} onClick={load}>Actualiser les demandes</button>
    </div>
    {!enabled && <p>Le simulateur est désactivé. Activez ENABLE_MOCK_PAYMENTS=true en développement.</p>}
    {error && <p role="alert" className="bo-alert bo-alert--error">{error}</p>}
    {notice && <p role="status" className="bo-alert bo-alert--success">{notice}</p>}
    {enabled && !loading && !items.length && <p className="bo-muted">Aucune transaction en attente.</p>}
    {enabled && items.length > 0 && <div className="bo-table-wrap">
      <table className="bo-table">
        <thead><tr><th>Référence</th><th>Opérateur</th><th>Numéro masqué</th>
          <th>Formule</th><th>Montant de test</th><th>Traitement</th></tr></thead>
        <tbody>{items.map(item => <tr key={item.id}>
          <td><code>{item.reference}</code></td>
          <td>{OPERATOR[item.provider] || item.provider}</td>
          <td>{item.phoneMasked}</td>
          <td>{item.planCode}</td>
          <td>{Number(item.amountXof || 0).toLocaleString('fr-FR')} FCFA</td>
          <td>{item.simulationMode === 'automatic' ? (
            <span className="bo-muted">Automatique · aucune intervention</span>
          ) : <div className="bo-actions">
            <button type="button" className="bo-button bo-button--primary"
              disabled={Boolean(busy)} onClick={() => setDecision({ item, outcome: 'success' })}>Simuler validation</button>
            <button type="button" className="bo-button bo-button--ghost"
              disabled={Boolean(busy)} onClick={() => setDecision({ item, outcome: 'failure' })}>Simuler refus</button>
          </div>}</td>
        </tr>)}</tbody>
      </table>
    </div>}
    <ConfirmDialog
      open={Boolean(decision)}
      title={decision?.outcome === 'success' ? 'Valider le paiement fictif' : 'Refuser le paiement fictif'}
      description="Cette décision simule la réponse de l'opérateur. Aucun paiement réel n'est effectué."
      details={decision && (
        <dl className="bo-confirm-dialog__summary">
          <div><dt>Référence</dt><dd>{decision.item.reference}</dd></div>
          <div><dt>Opérateur</dt><dd>{OPERATOR[decision.item.provider] || decision.item.provider}</dd></div>
          <div><dt>Numéro masqué</dt><dd>{decision.item.phoneMasked}</dd></div>
          <div><dt>Montant de test</dt>
            <dd>{Number(decision.item.amountXof || 0).toLocaleString('fr-FR')} FCFA</dd></div>
          <div><dt>Décision</dt>
            <dd>{decision.outcome === 'success' ? 'Accepter' : 'Refuser'}</dd></div>
        </dl>
      )}
      confirmLabel={decision?.outcome === 'success' ? 'Simuler la validation' : 'Simuler le refus'}
      busy={Boolean(busy)}
      onCancel={() => setDecision(null)}
      onConfirm={() => decision && resolve(decision.item, decision.outcome)}
    />
  </section>;
}
